import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'components/ui/toast-store'
import { useDropzone } from 'react-dropzone'
import { Upload } from 'lucide-react'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import {
	uploadDocument,
	uploadVersion,
} from 'slices/documentLibrarySlice'
import type {
	DocumentLibraryState,
	LibraryScopeArgs,
} from '../types/documentLibrary.types'
import { stripExtension, trimAndLowercase } from '../utils/filenameSanitize'
import UploadFileRow, {
	type UploadFileRowData,
	type UploadRowStatus,
} from './UploadFileRow'

const DEFAULT_MAX_BYTES = 524_288_000 // 500 MB — keep in sync with DOCUMENT_MAX_UPLOAD_BYTES on the backend
const MAX_BYTES =
	Number(
		typeof process !== 'undefined'
			? process.env?.REACT_APP_DOCUMENT_MAX_UPLOAD_BYTES
			: undefined,
	) || DEFAULT_MAX_BYTES

/**
 * MIME types accepted by react-dropzone. Mirrors backend's `fileFamily`
 * detection; the server is the source of truth, this is just a friendly
 * filter on the file picker.
 */
const ACCEPT: Record<string, string[]> = {
	'application/pdf': ['.pdf'],
	'application/msword': ['.doc'],
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
		'.docx',
	],
	'application/vnd.ms-excel': ['.xls'],
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
		'.xlsx',
	],
	'application/vnd.ms-powerpoint': ['.ppt'],
	'application/vnd.openxmlformats-officedocument.presentationml.presentation': [
		'.pptx',
	],
	'text/plain': ['.txt'],
	'text/csv': ['.csv'],
	'image/*': [],
	'video/*': [],
}

export interface ExistingLibraryDocument {
	id: string
	libraryName: string
}

type UploadFilesDialogProps = {
	open: boolean
	onClose: () => void
	scopeArgs: LibraryScopeArgs
	/**
	 * Existing docs in the same scope — used to detect new-version uploads.
	 * Parent computes via `matchesScope` so this stays scope-agnostic.
	 */
	existingDocuments: ExistingLibraryDocument[]
}

let progressKeyCounter = 0
const nextRowId = (): string =>
	`upload-${Date.now()}-${++progressKeyCounter}-${Math.random().toString(36).slice(2, 7)}`

const UploadFilesDialog: React.FC<UploadFilesDialogProps> = ({
	open,
	onClose,
	scopeArgs,
	existingDocuments,
}) => {
	const dispatch = useDispatch<any>()
	const [rows, setRows] = useState<UploadFileRowData[]>([])

	const progressByKey = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			state.libraryDocuments.uploadProgress,
	)

	const existingByLowerName = useMemo(() => {
		const map = new Map<string, string>()
		for (const doc of existingDocuments) {
			map.set(trimAndLowercase(doc.libraryName), doc.id)
		}
		return map
	}, [existingDocuments])

	const isAnyUploading = rows.some(r => r.status === 'uploading')
	const hasUnsubmittedRows = rows.some(
		r => r.status === 'pending' || r.status === 'error',
	)
	const allDone =
		rows.length > 0 && rows.every(r => r.status === 'success')

	const addFiles = useCallback(
		(files: File[]) => {
			if (!files.length) return
			setRows(prev => {
				const next = [...prev]
				for (const file of files) {
					next.push({
						id: nextRowId(),
						file,
						libraryName: stripExtension(file.name),
						changeNote: '',
						status: 'pending',
					})
				}
				return next
			})
		},
		[setRows],
	)

	const onDrop = useCallback(
		(accepted: File[]) => addFiles(accepted),
		[addFiles],
	)

	const { getRootProps, getInputProps, isDragActive, open: openFileDialog } =
		useDropzone({
			onDrop,
			accept: ACCEPT,
			noClick: true,
			noKeyboard: true,
			disabled: isAnyUploading,
		})

	const updateRow = useCallback(
		(id: string, patch: Partial<UploadFileRowData>) => {
			setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)))
		},
		[setRows],
	)

	const removeRow = useCallback(
		(id: string) => {
			setRows(prev => prev.filter(r => r.id !== id))
		},
		[setRows],
	)

	const rowsWithDerivedState = useMemo(() => {
		return rows.map(row => {
			const matchedDocId =
				existingByLowerName.get(trimAndLowercase(row.libraryName)) || null
			const matchesExisting = matchedDocId !== null
			const libraryNameInvalid = !row.libraryName.trim()
			const fileTooLarge = row.file.size > MAX_BYTES
			const changeNoteInvalid =
				matchesExisting && !row.changeNote.trim()
			const isInvalid =
				libraryNameInvalid || fileTooLarge || changeNoteInvalid
			return {
				row,
				matchedDocId,
				matchesExisting,
				libraryNameInvalid,
				fileTooLarge,
				changeNoteInvalid,
				isInvalid,
			}
		})
	}, [rows, existingByLowerName])

	const canSubmit =
		rows.length > 0 &&
		!isAnyUploading &&
		hasUnsubmittedRows &&
		rowsWithDerivedState.every(r => !r.isInvalid)

	const setRowStatus = useCallback(
		(id: string, status: UploadRowStatus, errorMessage?: string) => {
			updateRow(id, { status, errorMessage })
		},
		[updateRow],
	)

	const uploadOne = useCallback(
		async (
			row: UploadFileRowData,
			matchedDocId: string | null,
		): Promise<void> => {
			setRowStatus(row.id, 'uploading')
			try {
				if (matchedDocId) {
					await dispatch(
						uploadVersion({
							id: matchedDocId,
							file: row.file,
							changeNote: row.changeNote.trim(),
							progressKey: row.id,
						}),
					).unwrap()
				} else {
					await dispatch(
						uploadDocument({
							scopeArgs,
							file: row.file,
							libraryName: row.libraryName.trim(),
							changeNote: row.changeNote.trim() || undefined,
							progressKey: row.id,
						}),
					).unwrap()
				}
				setRowStatus(row.id, 'success')
			} catch (err: any) {
				const message =
					typeof err === 'string'
						? err
						: err?.message || 'Upload failed'
				setRowStatus(row.id, 'error', message)
			}
		},
		[dispatch, scopeArgs, setRowStatus],
	)

	const handleUpload = useCallback(async () => {
		const toUpload = rowsWithDerivedState.filter(
			r => !r.isInvalid && (r.row.status === 'pending' || r.row.status === 'error'),
		)
		// Concurrency 3 — small in-place limiter avoids pulling in p-limit.
		const queue = [...toUpload]
		const inflight = new Set<Promise<void>>()
		while (queue.length || inflight.size) {
			while (queue.length && inflight.size < 3) {
				const next = queue.shift()
				if (!next) break
				const p = uploadOne(next.row, next.matchedDocId).finally(() => {
					inflight.delete(p)
				})
				inflight.add(p)
			}
			if (inflight.size) await Promise.race(inflight)
		}
	}, [rowsWithDerivedState, uploadOne])

	const handleClose = useCallback(async () => {
		if (isAnyUploading) {
			const ok = await toast.confirm({
				title: 'Close upload dialog',
				message:
					'Uploads are still in progress. Close the dialog anyway? In-flight uploads will continue in the background.',
				confirmLabel: 'Close anyway',
				variant: 'warning',
			})
			if (!ok) return
		}
		// Reset state when fully closing.
		setRows([])
		onClose()
	}, [isAnyUploading, onClose])

	return (
		<Dialog open={open} onClose={handleClose}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<DialogTitle>Upload documents</DialogTitle>
					<DialogDescription>
						Drop files or browse to add them. Each file becomes a row;
						adjust the Library Name before uploading.
					</DialogDescription>
				</DialogHeader>

				<div className="px-5 py-4">
					<div
						{...getRootProps()}
						className={[
							'rounded-lg border-2 border-dashed p-6 text-center transition-colors',
							isDragActive
								? 'border-[#0080fe] bg-[#0080fe]/5'
								: 'border-gray-300 bg-gray-50/40',
							isAnyUploading ? 'opacity-50' : '',
						].join(' ')}
					>
						<input {...getInputProps()} />
						<Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
						<p className="text-sm text-gray-700">
							{isDragActive
								? 'Drop files to add them to the upload list…'
								: 'Drag and drop files here'}
						</p>
						<div className="mt-3">
							<Button
								type="button"
								variant="ghost"
								onClick={openFileDialog}
								disabled={isAnyUploading}
							>
								Browse files
							</Button>
						</div>
						<p className="text-xs text-gray-500 mt-3">
							Word, Excel, PowerPoint, PDF, text, and CSV up to{' '}
							{Math.floor(MAX_BYTES / 1024 / 1024)} MB each.
						</p>
					</div>

					{rows.length > 0 && (
						<div className="mt-4 max-h-[50vh] overflow-y-auto flex flex-col gap-3 pr-1">
							{rowsWithDerivedState.map(
								({
									row,
									matchedDocId,
									matchesExisting,
									libraryNameInvalid,
									fileTooLarge,
								}) => (
									<UploadFileRow
										key={row.id}
										row={row}
										progress={progressByKey[row.id]}
										matchesExisting={matchesExisting}
										libraryNameInvalid={libraryNameInvalid}
										fileTooLarge={fileTooLarge}
										disabled={isAnyUploading}
										onChangeLibraryName={value =>
											updateRow(row.id, { libraryName: value })
										}
										onChangeNote={value =>
											updateRow(row.id, { changeNote: value })
										}
										onRemove={() => removeRow(row.id)}
									/>
								),
							)}
						</div>
					)}
				</div>

				<DialogFooter>
					<Button
						type="button"
						variant={allDone ? 'success' : 'ghost'}
						onClick={handleClose}
					>
						{allDone ? 'Done' : 'Cancel'}
					</Button>
					<Button
						type="button"
						onClick={handleUpload}
						disabled={!canSubmit || allDone}
					>
						{isAnyUploading
							? 'Uploading…'
							: hasUnsubmittedRows && rows.some(r => r.status === 'error')
								? 'Retry failed'
								: `Upload ${rows.filter(r => r.status === 'pending').length || ''}`}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default UploadFilesDialog
